import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Play } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { MonacoJsonEditor } from '../../components/MonacoJsonEditor';
import { partnerApi, type HttpToolDefinition } from '../../lib/api';
import { CopyButton } from './CopyButton';
import { ScopeHelp } from './ScopeHelp';
import {
  TENANT_EXTERNAL_ID_TOKEN,
  PARTICIPANT_PHONE_TOKEN,
  CONTACT_EXTERNAL_ID_TOKEN,
  buildArgsExample,
  referencesContextSource,
} from './tool-inputs';
import { BASE_SCOPE, TOOL_SCOPE_OPTIONS, VERIFIED_SCOPE, type ToolScope } from './tool-scopes';
import { tenantPickerQuery } from './tool-test-tenants';
import { MAX_HISTORY, ToolTestHistory, type ToolTestRunRecord } from './ToolTestHistory';
import { ToolTestRunView } from './ToolTestRunView';
import styles from './ToolTestPanel.module.css';

const hostFromBaseUrl = (baseUrl: string): string =>
  URL.canParse(baseUrl) ? new URL(baseUrl).host : baseUrl;

interface HttpToolTestPanelProps {
  tool: HttpToolDefinition;
}

export const HttpToolTestPanel = ({ tool }: HttpToolTestPanelProps) => {
  const exampleArgs = useMemo(
    () => JSON.stringify(buildArgsExample(tool.request_schema, tool.request_mapping), null, 2),
    [tool.request_schema, tool.request_mapping],
  );

  const [businessId, setBusinessId] = useState('');
  const [partnerUserId, setPartnerUserId] = useState('');
  const [participantPhone, setParticipantPhone] = useState('');
  const [scope, setScope] = useState<ToolScope>(
    tool.required_scope === VERIFIED_SCOPE ? VERIFIED_SCOPE : BASE_SCOPE,
  );
  const [argsText, setArgsText] = useState(exampleArgs);
  const [argsValid, setArgsValid] = useState(true);
  const [runs, setRuns] = useState<ToolTestRunRecord[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const nextRunId = useRef(0);

  const { data: tenants } = useQuery(tenantPickerQuery);
  const tenantList = tenants?.data?.results ?? [];
  const selectedTenant = tenantList.find(tenant => tenant.id === businessId);

  const claimEnforced = Boolean(
    tool.claim_enforcement && Object.keys(tool.claim_enforcement).length > 0,
  );
  const toolTemplates = [tool.base_url, tool.path_template, tool.request_mapping];
  const phoneIsReachSourced = referencesContextSource(toolTemplates, 'participantPhone');
  // Resolving the contact ID needs a partner_user, same as claim enforcement does.
  const externalIdIsReachSourced = referencesContextSource(toolTemplates, 'contactExternalId');
  const partnerUserRequired = claimEnforced || externalIdIsReachSourced;
  const needsPartnerUser = partnerUserRequired && !partnerUserId.trim();
  const scopeInsufficient = tool.required_scope === VERIFIED_SCOPE && scope === BASE_SCOPE;

  const testMutation = useMutation({
    mutationFn: () => {
      const args = argsValid ? (JSON.parse(argsText) as Record<string, unknown>) : {};
      return partnerApi.testTool(tool.id, {
        businessId,
        partnerUserId: partnerUserId.trim() ? partnerUserId : undefined,
        participantPhone: participantPhone.trim() ? participantPhone.trim() : undefined,
        scope,
        args,
      });
    },
    onSuccess: ({ result }) => {
      nextRunId.current += 1;
      const record: ToolTestRunRecord = { id: nextRunId.current, at: new Date(), result };
      setRuns(previous => [record, ...previous].slice(0, MAX_HISTORY));
      setSelectedRunId(record.id);
    },
  });

  const runDisabled = !businessId || !argsValid || needsPartnerUser || testMutation.isPending;
  const selectedRun = runs.find(run => run.id === selectedRunId);

  const contactIdPlaceholder = externalIdIsReachSourced
    ? 'UUID — this tool sends the contact ID from Reach'
    : 'UUID — optional';
  const partnerUserPlaceholder = claimEnforced
    ? 'UUID — this tool enforces claims'
    : contactIdPlaceholder;
  const partnerUserMissingHint = claimEnforced
    ? 'This tool enforces claims — a contact UUID is required.'
    : `This tool sends the contact ID from Reach (${CONTACT_EXTERNAL_ID_TOKEN}) — a contact UUID is required to resolve it.`;

  const phoneHint = phoneIsReachSourced ? (
    <span className={styles.hint}>
      This tool sends the contact phone from Reach ({PARTICIPANT_PHONE_TOKEN}) — without one here, the
      run will fail validation.
    </span>
  ) : (
    <span className={styles.hint}>
      Stands in for the contact — fills {PARTICIPANT_PHONE_TOKEN} for tools that send the phone from
      Reach.
    </span>
  );

  return (
    <fieldset className={styles.panel}>
      <legend className={styles.legend}>Test runner</legend>
      <p className={styles.description}>
        Runs the saved tool against a real tenant and contact. Same code path as production — claim
        checks and credentials are evaluated for real.
      </p>
      <div className={styles.fields}>
        <div className={styles.field}>
          <label htmlFor="tool-test-tenant" className={styles.fieldLabel}>
            Tenant
          </label>
          <select
            id="tool-test-tenant"
            value={businessId}
            onChange={e => setBusinessId(e.target.value)}
            className={styles.selectFull}>
            <option value="">Pick a tenant…</option>
            {tenantList.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.id.slice(0, 8)}…)
              </option>
            ))}
          </select>
          {selectedTenant &&
            (selectedTenant.externalId ? (
              <span className={styles.externalIdRow}>
                External ID: <code>{selectedTenant.externalId}</code>
                <CopyButton value={selectedTenant.externalId} />
              </span>
            ) : (
              <span className={styles.hint}>
                This tenant has no external ID — tools that use {TENANT_EXTERNAL_ID_TOKEN} will fail
                validation.
              </span>
            ))}
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            Contact UUID {partnerUserRequired ? '(required)' : '(optional)'}
          </span>
          <input
            type="text"
            value={partnerUserId}
            onChange={e => setPartnerUserId(e.target.value)}
            className={styles.monoInput}
            placeholder={partnerUserPlaceholder}
          />
          {needsPartnerUser && <span className={styles.hint}>{partnerUserMissingHint}</span>}
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            Contact phone (simulate{phoneIsReachSourced ? ', required' : ', optional'})
          </span>
          <input
            type="text"
            value={participantPhone}
            onChange={e => setParticipantPhone(e.target.value)}
            className={styles.monoInput}
            placeholder="+15555550123"
          />
          {phoneHint}
        </label>

        <div className={styles.field}>
          <span className={styles.fieldLabelRow}>
            <label htmlFor="tool-test-scope" className={styles.fieldLabel}>
              Scope (simulate)
            </label>
            <ScopeHelp />
          </span>
          <select
            id="tool-test-scope"
            value={scope}
            onChange={e => setScope(e.target.value as ToolScope)}
            className={styles.select}>
            {TOOL_SCOPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {scopeInsufficient && (
            <span className={styles.hint}>
              You picked an unverified caller, so this run will be rejected — this tool needs a verified
              caller.
            </span>
          )}
        </div>

        <div className={styles.field}>
          <div className={styles.argsHeader}>
            <span className={styles.fieldLabel}>Args (JSON)</span>
            <button
              type="button"
              className={styles.resetButton}
              onClick={() => setArgsText(exampleArgs)}>
              Reset to example
            </button>
          </div>
          <div className={styles.fieldControl}>
            <MonacoJsonEditor
              value={argsText}
              onChange={setArgsText}
              onValidationChange={setArgsValid}
              height="120px"
            />
          </div>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            onClick={() => testMutation.mutate()}
            disabled={runDisabled}
            className={styles.primaryButton}>
            <Play />
            {testMutation.isPending ? 'Running…' : 'Run test'}
          </button>
          {testMutation.error && <span className={styles.errorText}>{testMutation.error.message}</span>}
        </div>

        {testMutation.isPending && (
          <div className={styles.pending}>
            <Loader2 className={styles.spinner} />
            Calling {tool.http_method.toUpperCase()} {hostFromBaseUrl(tool.base_url)}…
          </div>
        )}

        {!testMutation.isPending && selectedRun && <ToolTestRunView result={selectedRun.result} />}

        <ToolTestHistory runs={runs} selectedId={selectedRunId} onSelect={setSelectedRunId} />
      </div>
    </fieldset>
  );
};
